import { LowIncomeDeviationTableData } from './lowIncomeDeviationTable';

// Maximum income thresholds for low income deviation by number of children
const MAX_LOW_INCOME_THRESHOLDS = {
  1: 2450, // Last table entry with one_child data
  2: 2950, // Last table entry with two_children data
  3: 3350, // Last table entry with three_children data
  4: 3550, // Last table entry with four_children data
  5: 3750, // Last table entry with five_children data
  6: 3950  // Last table entry with six_children data
};

// Percentage thresholds for incomes at or below $1500
const PERCENTAGE_THRESHOLD = 1500;
const LOW_INCOME_PERCENTAGES = {
  1: 0.19, // 19% for 1 child
  2: 0.24, // 24% for 2 children
  3: 0.25, // 25% for 3 children
  4: 0.26, // 26% for 4 children
  5: 0.27, // 27% for 5 children
  6: 0.28  // 28% for 6 children
};

/**
 * Calculates the low income deviation adjustment for child support
 * @param adjustedIncome - Monthly adjusted income of the parent
 * @param numberOfChildren - Number of children subject to support
 * @param currentChildSupport - Current calculated child support amount
 * @returns Object with deviation amount and whether deviation applies
 */
export const calculateLowIncomeDeviation = (
  adjustedIncome: number,
  numberOfChildren: number,
  currentChildSupport: number
): {
  deviationAmount: number;
  deviationApplies: boolean;
  cappedAmount: number;
  explanation: string;
} => {
  // Validate number of children and cap at 6
  if (numberOfChildren < 1) {
    return {
      deviationAmount: 0,
      deviationApplies: false,
      cappedAmount: currentChildSupport,
      explanation: "Number of children must be at least 1"
    };
  }
  
  // Cap at 6 children for table lookup
  const effectiveChildCount = Math.min(numberOfChildren, 6);

  // Check if income qualifies for low income deviation
  const maxThreshold = MAX_LOW_INCOME_THRESHOLDS[effectiveChildCount as keyof typeof MAX_LOW_INCOME_THRESHOLDS];
  if (!maxThreshold || adjustedIncome > maxThreshold) {
    return {
      deviationAmount: 0,
      deviationApplies: false,
      cappedAmount: currentChildSupport,
      explanation: "Income exceeds low income deviation threshold"
    };
  }

  // Handle percentage-based calculation for incomes at or below $1500
  if (adjustedIncome <= PERCENTAGE_THRESHOLD) {
    const percentage = LOW_INCOME_PERCENTAGES[effectiveChildCount as keyof typeof LOW_INCOME_PERCENTAGES];
    if (percentage) {
      const maxAllowedSupport = adjustedIncome * percentage;
      
      if (currentChildSupport > maxAllowedSupport) {
        const deviationAmount = currentChildSupport - maxAllowedSupport;
        return {
          deviationAmount: -deviationAmount,
          deviationApplies: true,
          cappedAmount: maxAllowedSupport,
          explanation: `Child support reduced from $${currentChildSupport.toFixed(2)} to $${maxAllowedSupport.toFixed(2)} (${(percentage * 100)}% of income) due to low income deviation`
        };
      }
      
      return {
        deviationAmount: 0,
        deviationApplies: false,
        cappedAmount: currentChildSupport,
        explanation: `Current child support is within low income deviation limits (${(percentage * 100)}% of income = $${(adjustedIncome * percentage).toFixed(2)})`
      };
    }
  }

  // Validate number of children and cap at 6
  if (numberOfChildren < 1) {
    return {
      deviationAmount: 0,
      deviationApplies: false,
      cappedAmount: currentChildSupport,
      explanation: "Number of children must be at least 1"
    };
  }

  // Find the appropriate table entry based on income
  let tableEntry = null;
  let minDifference = Infinity;
  
  // Find closest table entry
  for (const entry of LowIncomeDeviationTableData) {
    const tableIncome = parseFloat(entry.adjusted.replace(/,/g, ''));
    const difference = Math.abs(adjustedIncome - tableIncome);
    
    if (difference < minDifference) {
      minDifference = difference;
      tableEntry = entry;
    }
  }

  // If no table entry found, no deviation applies
  if (!tableEntry) {
    return {
      deviationAmount: 0,
      deviationApplies: false,
      cappedAmount: currentChildSupport,
      explanation: "No applicable table entry found"
    };
  }

  // Get the maximum allowed child support for this income and number of children
  let maxAllowedSupport: number | null = null;
  
  switch (effectiveChildCount) {
    case 1:
      maxAllowedSupport = tableEntry.one_child ? parseFloat(tableEntry.one_child.replace(/,/g, '')) : null;
      break;
    case 2:
      maxAllowedSupport = tableEntry.two_children ? parseFloat(tableEntry.two_children.replace(/,/g, '')) : null;
      break;
    case 3:
      maxAllowedSupport = tableEntry.three_children ? parseFloat(tableEntry.three_children.replace(/,/g, '')) : null;
      break;
    case 4:
      maxAllowedSupport = tableEntry.four_children ? parseFloat(tableEntry.four_children.replace(/,/g, '')) : null;
      break;
    case 5:
      maxAllowedSupport = tableEntry.five_children ? parseFloat(tableEntry.five_children.replace(/,/g, '')) : null;
      break;
    case 6:
      maxAllowedSupport = tableEntry.six_children ? parseFloat(tableEntry.six_children.replace(/,/g, '')) : null;
      break;
  }

  // If no cap exists for this child count at this income level, no deviation applies
  if (maxAllowedSupport === null) {
    return {
      deviationAmount: 0,
      deviationApplies: false,
      cappedAmount: currentChildSupport,
      explanation: `No low income deviation cap for ${effectiveChildCount} children at this income level`
    };
  }

  // Check if current support exceeds the table amount
  if (currentChildSupport > maxAllowedSupport) {
    const deviationAmount = currentChildSupport - maxAllowedSupport;
    return {
      deviationAmount: -deviationAmount, // Negative because it's a reduction
      deviationApplies: true,
      cappedAmount: maxAllowedSupport,
      explanation: `Child support reduced from $${currentChildSupport.toFixed(2)} to $${maxAllowedSupport.toFixed(2)} due to low income deviation`
    };
  }

  // Current support is within acceptable range
  return {
    deviationAmount: 0,
    deviationApplies: false,
    cappedAmount: currentChildSupport,
    explanation: "Current child support is within low income deviation limits"
  };
};

/**
 * Gets the maximum allowed child support for a given income and number of children
 * @param adjustedIncome - Monthly adjusted income of the parent
 * @param numberOfChildren - Number of children subject to support
 * @returns Maximum allowed child support amount or null if not applicable
 */
export const getLowIncomeDeviationCap = (
  adjustedIncome: number,
  numberOfChildren: number
): number | null => {
  // Handle percentage-based calculation for incomes at or below $1500
  if (adjustedIncome <= PERCENTAGE_THRESHOLD) {
    const percentage = LOW_INCOME_PERCENTAGES[numberOfChildren as keyof typeof LOW_INCOME_PERCENTAGES];
    return percentage ? adjustedIncome * percentage : null;
  }
  
  // For incomes above $1500, use table lookup
  const maxThreshold = MAX_LOW_INCOME_THRESHOLDS[numberOfChildren as keyof typeof MAX_LOW_INCOME_THRESHOLDS];
  if (maxThreshold && adjustedIncome <= maxThreshold) {
    let tableEntry = null;
    let minDifference = Infinity;
    
    // Find closest table entry
    for (const entry of LowIncomeDeviationTableData) {
      const tableIncome = parseFloat(entry.adjusted.replace(/,/g, ''));
      const difference = Math.abs(adjustedIncome - tableIncome);
      
      if (difference < minDifference) {
        minDifference = difference;
        tableEntry = entry;
      }
    }

    if (tableEntry) {
      switch (numberOfChildren) {
        case 1: return tableEntry.one_child ? parseFloat(tableEntry.one_child.replace(/,/g, '')) : null;
        case 2: return tableEntry.two_children ? parseFloat(tableEntry.two_children.replace(/,/g, '')) : null;
        case 3: return tableEntry.three_children ? parseFloat(tableEntry.three_children.replace(/,/g, '')) : null;
        case 4: return tableEntry.four_children ? parseFloat(tableEntry.four_children.replace(/,/g, '')) : null;
        case 5: return tableEntry.five_children ? parseFloat(tableEntry.five_children.replace(/,/g, '')) : null;
        case 6: return tableEntry.six_children ? parseFloat(tableEntry.six_children.replace(/,/g, '')) : null;
      }
    }
  }

  return null;
};

export { MAX_LOW_INCOME_THRESHOLDS, PERCENTAGE_THRESHOLD, LOW_INCOME_PERCENTAGES };